/**
 * AgentTopbar — primary surface. Morph-bar pattern (per user-supplied
 * reference component, 2026-05-06): a single always-mounted DOM node
 * morphs between a closed pill and a centered command palette.
 *
 *   closed:   [✨ Ask the agent…  ⌘K]   (centered topbar pill)
 *   ⌘K / click → smooth width/height/border-radius animation
 *   open:    ┌────────────────────────────────────────┐
 *            │ ✨  Ask the agent or search…  esc  ✕  │
 *            ├────────────────────────────────────────┤
 *            │ SUGGESTED  +chip +chip +chip           │
 *            │ RECENT     ▸ row · meta                │
 *            │ JUMP TO    ▸ row · ⌘N                  │
 *            │ ↑↓ Nav · ↵ Select · ⌘K Toggle · esc    │
 *            └────────────────────────────────────────┘
 *
 * On submit the body crossfades from the empty/querying state into
 * <ConversationView>; the morph stays the same dimensions, so chat
 * continues in-place. A "+ New" button in chat mode resets the
 * conversation. Width/height animate identically across breakpoints.
 *
 * Mobile (<640px): the same morph adapts via media query —
 * full-width closed pill, near-fullscreen open palette, X close
 * button in the input row replaces the keyboard-only esc shortcut.
 *
 * Hard constraint: the morph element must stay mounted across open/
 * close, so it's portaled to document.body (in `<AgentSearch>`) — no
 * Radix Dialog wrapper, which would unmount on close.
 */

import { AppBar } from '@ordo/ui/app-bar'
import { useShellLayout } from '@ordo/ui/app-shell'
import { Calendar, CheckCircle2, FolderKanban, MessageSquare, Plus } from 'lucide-react'
import { type ReactNode, useMemo } from 'react'

import { AgentSearch, type AgentSearchSection, type AgentSearchSuggestion } from '../agent-search/agent-search'
import { useEmbedConfig } from '../hooks/use-embed-config'
import { configureAgentToken, configureApiBase } from '../lib/api-client'
import { chatSearchKind } from '../lib/chat-search-kind'
import { starterPromptsFor } from '../lib/default-suggestions'
import { relativeTime, useRecentConversations } from '../lib/recent-conversations'
import { useAgentStore } from '../stores/agent-store'
import type { AgentEmbedConfig, AgentSurfaceProps } from '../types'
import { ConversationProvider } from './conversation-provider'

const REMOVED_EMPTY_STATE_PROMPTS = new Set([
  "what's the decided path for the chef-ops-nutrition person making recipes/products?",
])

export function AgentTopbar(props: AgentSurfaceProps) {
  // Configure API base URL + agent-token SYNCHRONOUSLY during render,
  // before any child hook fires its first fetch. The previous useEffect
  // approach ran after the first render, so useRecentConversations
  // (called inside useTopbarSections) issued its initial SWR fetch
  // with an empty base — the URL went same-origin to the host app's
  // /api/recall/conversations and 404'd on every host except the
  // agent itself, surfacing as a "Request failed (404)" toast.
  // configureApiBase is idempotent + cheap; running it during render
  // is fine.
  if (props.apiBaseUrl !== undefined) {
    configureApiBase(props.apiBaseUrl)
  }
  configureAgentToken(props.getAgentToken ?? null)

  // Pull org-wide defaults set in /admin/embed. Module-cached per host,
  // so this shares its result with VoiceButton (which reads the host
  // from the agent store and calls the hook — same key, cache wins).
  const serverConfig = useEmbedConfig(props.hostApp, props.apiBaseUrl)

  // New: prefer the typed `config` prop. When absent, synthesize one
  // from the legacy props (searchPages, agentSuggestions, etc.) so
  // existing host-app mounts keep working unchanged. Phase G removes
  // the legacy path once every host migrates.
  const config = useMemo(() => {
    const merged = mergeServerConfig(props.config ?? synthesizeLegacyConfig(props), serverConfig)
    // [PROD-1204] Chat search, appended last so the host's own entities always
    // outrank chats in the palette. Registering it here rather than in each
    // host's config is what makes the feature cross-platform for free — and
    // legacy-prop hosts get `searchable: []` from synthesizeLegacyConfig, so
    // for them this is the kind that makes live search fire at all.
    return { ...merged, searchable: [...merged.searchable, chatSearchKind(merged.agentAppUrl)] }
  }, [props, serverConfig])

  // Unified shell (opt-in internal-tools layout): search sits at the far
  // right of the top bar, past the host's bell.
  const unified = useShellLayout() === 'unified'
  const suggestions = useTopbarSuggestions(props.hostApp, config, props.hostContext)
  const sections = useTopbarSections(props.hostApp, config)

  // Kill switch — admin disabled the embed org-wide. Dropping the search
  // unmounts <AgentSearch>, which owns the ⌘K listener, so the shortcut
  // goes silent across every host.
  const withAppBar = props.appBar !== false
  const search = serverConfig?.disabled ? null : (
    <ConversationProvider hostApp={props.hostApp}>
      <AgentSearch
        suggestions={suggestions}
        sections={sections}
        slashCommands={config.slashCommands ?? props.slashCommands}
        config={config}
        compact={withAppBar}
      />
    </ConversationProvider>
  )
  if (!withAppBar) {
    return search
  }
  if (unified) {
    // `display: contents` makes the app bar and search direct flex children
    // of the top bar, so `order` can move search after AppTopBarRight (the
    // bell) while the app bar keeps the space on the left.
    return (
      <div style={{ display: 'contents' }}>
        <AppBar />
        {search ? <div style={{ order: 99, display: 'flex', alignItems: 'center' }}>{search}</div> : null}
      </div>
    )
  }
  // PROD-1403 layout: the app bar takes the middle of the top bar and the
  // compact Search sits at its right edge, next to the host's bell
  // (AppTopBarRight). The app bar stays even when the embed is disabled.
  return (
    <div style={{ display: 'flex', flex: '1 1 auto', minWidth: 0, alignItems: 'center', gap: 8 }}>
      <AppBar />
      {search}
    </div>
  )
}

/** Merge org-wide defaults from /admin/embed under explicit host
 *  config. Today: clears `crossApp` when the admin turned cross-app
 *  search off, and fills in `suggestions` when the host hasn't
 *  supplied any. Voice input gating lives inside VoiceButton (it
 *  reads the same hook directly via the module cache). */
function mergeServerConfig(
  base: AgentEmbedConfig,
  server: { crossAppSearch: boolean; suggestions: string[] } | undefined,
): AgentEmbedConfig {
  if (!server) {
    return base
  }
  const merged: AgentEmbedConfig = { ...base }
  if (!server.crossAppSearch) {
    delete merged.crossApp
  }
  const explicitSuggestions =
    typeof base.suggestions === 'function' ? true : Array.isArray(base.suggestions) && base.suggestions.length > 0
  if (!explicitSuggestions && server.suggestions.length > 0) {
    merged.suggestions = server.suggestions
  }
  return merged
}

/** Build a config from legacy AgentSurfaceProps so callers using
 *  the old prop shape don't break. Drops behavior the new config
 *  type doesn't fully express (e.g. flat `searchEntities` callback)
 *  — that path stays through the legacy field on the synthesized
 *  config so EmptyBody can fall back to it until Phase A.2 ships
 *  the search runner. */
function synthesizeLegacyConfig(props: AgentSurfaceProps): AgentEmbedConfig {
  return {
    pages: (props.searchPages ?? []).map((p) => ({
      id: `page-${p.href}`,
      label: p.label,
      ...(p.description !== undefined ? { description: p.description } : {}),
      href: p.href,
      ...(p.icon !== undefined ? { icon: p.icon } : {}),
      ...(p.keywords !== undefined ? { keywords: p.keywords } : {}),
    })),
    suggestions: props.agentSuggestions ?? [],
    // Legacy `searchEntities` returns one flat array; we leave
    // searchable empty until A.2 lifts that path into a kinded
    // wrapper. Until then EmptyBody continues to use the existing
    // static section rendering.
    searchable: [],
    ...(props.slashCommands !== undefined ? { slashCommands: props.slashCommands } : {}),
  }
}

function useTopbarSuggestions(
  hostApp: string,
  config: AgentEmbedConfig,
  hostContext: Record<string, unknown> | undefined,
): AgentSearchSuggestion[] {
  return useMemo(() => {
    const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
    const raw =
      typeof config.suggestions === 'function'
        ? config.suggestions({ ...(hostContext !== undefined ? { hostContext } : {}), pathname })
        : config.suggestions
    if (raw && raw.length > 0) {
      return raw
        .filter((label) => !isRemovedEmptyStatePrompt(label))
        .slice(0, 4)
        .map((label, idx) => ({ id: `${hostApp}-${idx}`, label }))
    }
    return starterPromptsFor(hostApp)
      .filter((starter) => !isRemovedEmptyStatePrompt(starter.label))
      .map((s) => ({ id: s.id, label: s.label }))
  }, [hostApp, config, hostContext])
}

function useTopbarSections(hostApp: string, config: AgentEmbedConfig): AgentSearchSection[] {
  // Scope RECENT to the host app the embed is mounted in: Tasker shows
  // Tasker chats, RFP-Gremlin shows RFP-Gremlin chats, etc. The agent
  // service stores `hostApp` on each RecallConversation; the API
  // filters when `?hostApp=…` is present.
  const { conversations } = useRecentConversations(3, hostApp)
  const setConversationId = useAgentStore((s) => s.setConversationId)
  const setPaletteOverride = useAgentStore((s) => s.setPaletteOverride)

  return useMemo(() => {
    const out: AgentSearchSection[] = []

    if (conversations.length > 0) {
      out.push({
        id: 'recent',
        label: 'Recent',
        items: conversations
          .map((c) => ({
            id: `conv-${c.id}`,
            title: c.title ?? 'Untitled chat',
            icon: <MessageSquare size={12} />,
            tint: 'violet' as const,
            meta: relativeTime(c.updatedAt),
            // Clicking a RECENT row opens that chat *in place* — flip
            // paletteOverride off so the morph re-renders into chat
            // mode as soon as the conversation hydrates. Don't close
            // the morph (keepOpenOnSelect) — closing would hide the
            // very thing the user clicked.
            onSelect: () => {
              setPaletteOverride(false)
              setConversationId(c.id)
            },
            keepOpenOnSelect: true,
          }))
          .filter((item) => !isRemovedEmptyStatePrompt(item.title)),
      })
    }

    if (config.pages.length > 0) {
      out.push({
        id: 'jump',
        label: 'Jump to',
        items: config.pages.slice(0, 6).map((p) => ({
          id: p.id,
          title: p.label,
          icon: (p.icon as ReactNode) ?? <FolderKanban size={12} />,
          tint: 'blue' as const,
          href: p.href,
        })),
      })
    }

    if (out.length === 0) {
      // Fallback so the open palette never feels empty for first-time users.
      out.push({
        id: 'starters',
        label: 'Try',
        items: [
          { id: 'plan-week', title: 'Plan my week', icon: <Calendar size={12} />, tint: 'amber' as const },
          { id: 'create-task', title: 'Create a new item', icon: <Plus size={12} />, tint: 'pink' as const },
          {
            id: 'whats-shipped',
            title: "What's shipped lately?",
            icon: <CheckCircle2 size={12} />,
            tint: 'green' as const,
          },
        ],
      })
    }

    return out.filter((section) => section.items.length > 0)
  }, [conversations, config.pages, setConversationId])
}

function isRemovedEmptyStatePrompt(value: string | null | undefined): boolean {
  if (!value) {
    return false
  }
  return REMOVED_EMPTY_STATE_PROMPTS.has(value.trim().replace(/\s+/g, ' ').toLowerCase())
}
